import styled from "@emotion/styled";
import { ClickAwayListener } from "@mui/base";
import { Add, ArrowDownward, ArrowUpward, Edit } from "@mui/icons-material";
import { Box, Link, Table as MuiTable, Option, Select, Stack } from "@mui/joy";
import { Outlet, useNavigate, useParams } from "@tanstack/react-router";
import {
  CellContext,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Header as HeaderType,
  Row,
  Table,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { ApiLine, ApiZone } from "griptonite-api-client";
import { memo, useState } from "react";

import {
  useOptimisticUpdateLineZoneMutation,
  useVenueLinesQuery,
  useVenueZonesByClimbType,
} from "@/hooks/routes";
import { useCurrentVenueUuid } from "@/hooks/venues";
import formatNumber from "@/utils/formatNumber";

import { ButtonLink } from "../Links";

type TableType = Table<ApiLine>;

const filterActiveSortNatural = (lines: ApiLine[]) =>
  lines
    .filter((l) => l.isActive)
    .sort((a, b) => {
      return a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });

const useCurrentLineUuid = () => {
  return useParams({ strict: false }).lineUuid;
};

export default function ManageLines() {
  const venueUuid = useCurrentVenueUuid();
  const { data: lines } = useVenueLinesQuery(venueUuid, filterActiveSortNatural);

  return (
    <Stack direction="row" sx={{ height: "100dvh" }}>
      <Box sx={{ overflow: "auto", height: "100%" }}>
        <LinesTable lines={lines} />
      </Box>
      <Box>
        <Outlet />
      </Box>
    </Stack>
  );
}

const StyledTh = styled.th`
  &.active {
    background: #e5efff;
    position: relative;
    border: 1px solid #0084ff;
    border-bottom-width: 1px !important;
  }
`;

const columnHelper = createColumnHelper<ApiLine>();

const TableHeader = ({ header }: { header: HeaderType<ApiLine, unknown>; table: TableType }) => {
  const isSorted = header.column.getIsSorted();
  return (
    <StyledTh>
      <Link
        underline="none"
        textColor={header.column.getIsSorted() ? "primary.500" : "neutral.800"}
        onClick={header.column.getToggleSortingHandler()}
        startDecorator={isSorted ? isSorted === "desc" ? <ArrowDownward /> : <ArrowUpward /> : null}
        sx={{ flex: 1 }}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
      </Link>
    </StyledTh>
  );
};

const TableRow = memo(({ row }: { row: Row<ApiLine>; table: TableType }) => {
  const navigate = useNavigate();
  const currentLineUuid = useCurrentLineUuid();
  return (
    <tr
      role="link"
      onClick={() =>
        navigate({
          from: "/gym/$venueUuid/settings/lines",
          to: "./$lineUuid",
          params: { lineUuid: row.original.uuid },
          replace: true,
        })
      }
      style={{
        cursor: "pointer",
        ...(currentLineUuid === row.original.uuid
          ? {
              "--TableCell-dataBackground": "var(--TableCell-selectedBackground)",
              "--TableCell-headBackground": "var(--TableCell-selectedBackground)",
            }
          : {}),
      }}
    >
      {row.getVisibleCells().map((cell) => {
        return <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>;
      })}
    </tr>
  );
});
TableRow.displayName = "TableRow";

const ZoneCell = memo((props: CellContext<ApiLine, string>) => {
  const [isEditing, setIsEditing] = useState(false);
  if (!isEditing) {
    return (
      <Stack
        direction="row"
        gap={0.5}
        sx={{
          cursor: "pointer",
          ".MuiSvgIcon-root": {
            display: "none",
          },
          "&:hover .MuiSvgIcon-root": {
            display: "block",
          },
          width: "100%",
          height: "100%",
        }}
        alignItems="center"
        onClick={() => setIsEditing(true)}
      >
        {props.getValue()}
        <Edit />
      </Stack>
    );
  }
  return (
    <ClickAwayListener onClickAway={() => setIsEditing(false)}>
      <Box>
        <ZoneCellEditing line={props.row.original} onFinish={() => setIsEditing(false)} />
      </Box>
    </ClickAwayListener>
  );
});
ZoneCell.displayName = "ZoneCell";

const ZoneCellEditing = ({ line, onFinish }: { line: ApiLine; onFinish: () => void }) => {
  const venueUuid = useCurrentVenueUuid();
  const { data: zones } = useVenueZonesByClimbType(venueUuid, "SPORT");
  const mutation = useOptimisticUpdateLineZoneMutation();

  const onChange = (_e: unknown, value: string | null) => {
    let zone: ApiZone | null = null;
    if (value) {
      zone = zones?.find((z) => z.uuid === value) ?? null;
      if (!zone) {
        throw new Error("Zone not found");
      }
    }
    mutation.mutate({
      uuid: line.uuid,
      zone,
    });
    onFinish();
  };

  return (
    <Select size="sm" listboxOpen onClose={onFinish} onChange={onChange} value={line.zone?.uuid}>
      {zones?.map((zone) => (
        <Option key={zone.uuid} value={zone.uuid}>
          {zone.name}
        </Option>
      ))}
      {line.zone ? <Option value={null}>(Clear)</Option> : null}
    </Select>
  );
};

const LinesTable = memo(({ lines }: { lines: ApiLine[] | undefined }) => {
  const table = useReactTable<ApiLine>({
    columns: [
      columnHelper.accessor("name", {
        header: "Name",
        id: "name",
        cell: (props) => <strong>{props.getValue()}</strong>,
      }),
      columnHelper.accessor("zone.name", {
        header: "Sector",
        id: "sector",
        cell: ZoneCell,
      }),
      columnHelper.accessor("height", {
        header: "Height",
        cell: (props) => {
          const heightCm = props.getValue();
          if (heightCm) {
            return formatNumber(props.getValue() / 100, { maxDecimals: 1 }) + "m";
          }
          return "–";
        },
      }),
      columnHelper.accessor("quickdrawCount", {
        header: "Quickdraws",
      }),
      columnHelper.accessor("canLead", {
        header: "Lead",
        cell: (props) => (props.getValue() ? "✔️" : null),
      }),
      columnHelper.accessor("canToprope", {
        header: "Toprope",
        cell: (props) => (props.getValue() ? "✔️" : null),
      }),
      columnHelper.accessor("autoBelay", {
        header: "Auto Belay",
        cell: (props) => (props.getValue() ? "✔️" : null),
      }),
      columnHelper.accessor("routesCount", {
        header: "Routes",
      }),
      columnHelper.accessor("lastSetTime", {
        header: "Last Set",
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.lastSetTime ? new Date(rowA.original.lastSetTime).getTime() : 0;
          const b = rowB.original.lastSetTime ? new Date(rowB.original.lastSetTime).getTime() : 0;
          return b - a;
        },
        cell: (props) => {
          const s = props.getValue();
          if (!s) {
            return "–";
          }
          const d = new Date(s);
          if (d.getFullYear() === new Date().getFullYear()) {
            return format(new Date(props.getValue()), "do MMM");
          } else {
            return format(new Date(props.getValue()), "do MMM yyyy");
          }
        },
      }),
    ],
    data: lines || [],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    initialState: {
      sorting: [
        { id: "zone", desc: false },
        { id: "name", desc: false },
      ],
    },
  });

  return (
    <MuiTable
      borderAxis="bothBetween"
      hoverRow
      sx={{
        td: { position: "relative" },
        "th, td": {
          paddingX: 1,
          paddingY: 1,
        },
        th: {
          backgroundColor: "neutral.100",
        },
        "--TableRow-hoverBackground": (theme) => theme.vars.palette.rowHoverBackground,
        "--TableCell-selectedBackground": (theme) => theme.vars.palette.primary.softBg,
      }}
    >
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return <TableHeader key={header.id} header={header} table={table} />;
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => {
          return <TableRow key={row.id} row={row} table={table} />;
        })}
        <tr>
          <td colSpan={table.getAllColumns().length}>
            <ButtonLink
              to="/gym/$venueUuid/settings/lines/create"
              from="/gym/$venueUuid"
              params={true}
              startDecorator={<Add />}
            >
              Add new line
            </ButtonLink>
          </td>
        </tr>
      </tbody>
    </MuiTable>
  );
});
LinesTable.displayName = "LinesTable";
